<p-toast key="{{ messageKey }}"></p-toast>

<!-- Page header -->
<div *transloco="let t" class="pwc-linear-gradient px-8 pt-6 pb-14">
  <div class="flex items-center justify-between">
    <h1 class="text-white font-serif font-extralight text-3xl tracking-wide">
      {{ t("page_transaction_query_details") }}
    </h1>
    <a
      routerLink="/status-monitor/transactions"
      class="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg border border-white/20 transition-all duration-200"
    >
      <span class="icon-arrowLeft-fill text-sm"></span>
      {{ t("back") }}
    </a>
  </div>
</div>

<div class="px-8 pb-10 -mt-8" *transloco="let t">
  <!-- Loading skeleton -->
  @if (loading) {
    <div class="bg-white rounded-xl shadow-lg p-8">
      <p-skeleton width="12rem" styleClass="mb-4"></p-skeleton>
      <p-skeleton width="8rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton height=".5rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton width="10rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton height=".5rem"></p-skeleton>
    </div>
  }

  <!-- Details content -->
  @if (!loading && queryDetails) {
    <div class="bg-white rounded-xl shadow-lg overflow-hidden">
      <!-- Request info header -->
      <div
        class="px-6 py-4 border-b border-grey-light flex items-center justify-between"
      >
        <div class="flex items-center gap-3">
          <span
            class="icon-downloadBox-fill text-orange-default text-lg"
          ></span>
          <span class="font-semibold text-grey-dark">
            {{ queryDetails.engagementName }}
          </span>
          <span
            class="text-xs font-mono px-2 py-0.5 rounded bg-grey-lighter text-grey-default"
          >
            {{ queryDetails.jobCode }}
          </span>
        </div>
        <span
          class="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full"
          [ngClass]="getStatusClass(queryDetails.status)"
        >
          {{ queryDetails.status }}
        </span>
      </div>

      <!-- Details grid -->
      <div class="p-6">
        <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          <!-- General info -->
          <div class="space-y-4">
            <h3
              class="text-xs font-semibold text-grey-default uppercase tracking-wider mb-3"
            >
              {{ t("general_info") }}
            </h3>
            <div class="space-y-3">
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("name")
                }}</span>
                <span class="text-sm font-medium text-grey-dark">{{
                  queryDetails.engagementName
                }}</span>
              </div>
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("jobcode")
                }}</span>
                <span class="text-sm font-mono text-grey-dark">{{
                  queryDetails.jobCode
                }}</span>
              </div>
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("vatnumber")
                }}</span>
                <span class="text-sm font-mono text-grey-dark">{{
                  queryDetails.vatNumber
                }}</span>
              </div>
              <div>
                <span class="text-xs text-grey-medium block">IBAN</span>
                <span class="text-sm font-mono text-grey-dark">{{
                  queryDetails.iban
                }}</span>
              </div>
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("queried_date")
                }}</span>
                <span class="text-sm text-grey-dark">{{
                  queryDetails.created | date: "yyyy.MM.dd HH:mm"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Dataset report -->
          <div class="space-y-4">
            <h3
              class="text-xs font-semibold text-grey-default uppercase tracking-wider mb-3"
            >
              Dataset
            </h3>
            <div class="space-y-3">
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("requested")
                }}</span>
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded"
                  [ngClass]="
                    queryDetails.datasetRequested
                      ? 'bg-green-lighter text-status-green'
                      : 'bg-grey-lighter text-grey-default'
                  "
                >
                  {{ queryDetails.datasetRequested ? t("yes") : t("no") }}
                </span>
              </div>
              @if (queryDetails.datasetRequested) {
                <div>
                  <span class="text-xs text-grey-medium block">{{
                    t("from_date")
                  }}</span>
                  <span class="text-sm text-grey-dark">{{
                    queryDetails.datasetFromDate | date: "yyyy.MM.dd"
                  }}</span>
                </div>
                <div>
                  <span class="text-xs text-grey-medium block">{{
                    t("to_date")
                  }}</span>
                  <span class="text-sm text-grey-dark">{{
                    queryDetails.datasetToDate | date: "yyyy.MM.dd"
                  }}</span>
                </div>
              }
            </div>
          </div>

          <!-- Power BI report -->
          <div class="space-y-4">
            <h3
              class="text-xs font-semibold text-grey-default uppercase tracking-wider mb-3"
            >
              Power BI
            </h3>
            <div class="space-y-3">
              <div>
                <span class="text-xs text-grey-medium block">{{
                  t("requested")
                }}</span>
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded"
                  [ngClass]="
                    queryDetails.powerBiRequested
                      ? 'bg-green-lighter text-status-green'
                      : 'bg-grey-lighter text-grey-default'
                  "
                >
                  {{ queryDetails.powerBiRequested ? t("yes") : t("no") }}
                </span>
              </div>
              @if (queryDetails.powerBiRequested) {
                <div>
                  <span class="text-xs text-grey-medium block">{{
                    t("from_date")
                  }}</span>
                  <span class="text-sm text-grey-dark">{{
                    queryDetails.powerBiFromDate | date: "yyyy.MM.dd"
                  }}</span>
                </div>
                <div>
                  <span class="text-xs text-grey-medium block">{{
                    t("to_date")
                  }}</span>
                  <span class="text-sm text-grey-dark">{{
                    queryDetails.powerBiToDate | date: "yyyy.MM.dd"
                  }}</span>
                </div>
                @if (queryDetails.powerBiLink) {
                  <div>
                    <span class="text-xs text-grey-medium block">{{
                      t("view_report")
                    }}</span>
                    <a
                      [href]="queryDetails.powerBiLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-sm text-orange-default hover:underline"
                    >
                      {{ t("view_report") }}
                      <span
                        class="icon-externalLink-outline text-xs ml-1"
                      ></span>
                    </a>
                  </div>
                }
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Reports section -->
      @if (
        queryDetails.status === "Completed" && queryDetails.results.length > 0
      ) {
        <div class="border-t border-grey-light p-6">
          <h3
            class="text-xs font-semibold text-grey-default uppercase tracking-wider mb-4"
          >
            {{ t("report_section") }}
          </h3>
          <div class="space-y-2">
            @for (result of queryDetails.results; track result.id) {
              <div
                class="flex items-center justify-between p-3 rounded-lg border border-grey-light hover:bg-grey-lighter/50 transition-colors"
              >
                <div class="flex items-center gap-3">
                  <span
                    class="icon-downloadSave-fill text-orange-default"
                  ></span>
                  <span class="text-sm font-medium text-grey-dark">{{
                    result.reportName
                  }}</span>
                </div>
                <div class="flex items-center gap-4">
                  @if (result.validTo) {
                    <span class="text-xs text-grey-medium">
                      {{ t("valid_to") }}: {{ result.validTo }}
                    </span>
                  }
                  @if (result.isAvailable) {
                    <button
                      (click)="downloadReport(result)"
                      class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-default hover:bg-orange-dark rounded-lg transition-colors"
                    >
                      <span class="icon-downloadSave-fill text-xs"></span>
                      {{ t("download") }}
                    </button>
                  } @else {
                    <span class="text-xs text-grey-medium italic">
                      {{ t("not_available") }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- No results message for completed queries -->
      @if (
        queryDetails.status === "Completed" && queryDetails.results.length === 0
      ) {
        <div class="border-t border-grey-light p-6">
          <div class="flex flex-col items-center py-6 text-center">
            <span
              class="icon-downloadBox-fill text-3xl text-grey-light mb-2"
            ></span>
            <p class="text-sm text-grey-medium">{{ t("no_reports") }}</p>
          </div>
        </div>
      }
    </div>
  }
</div>
